// Signup.js
import React, { useEffect, useState } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, setDoc, getDocs, collection, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

/* ─── SAME LOGIN THEME TOKENS ─── */
const THEMES = {
  light: {
    bg: "#ffffff",
    bgSub: "#f7f7f8",
    surface: "#f0f0f2",
    surfaceHover: "#e8e8eb",
    border: "#e3e3e6",
    borderStrong: "#c5c5ca",
    text: "#111111",
    textSub: "#5f5f6b",
    textMuted: "#a3a3b0",
    userBubble: "#1a1a1a",
    userText: "#ffffff",
    aiBubble: "#f7f7f8",
    aiBorder: "#e8e8eb",
    aiText: "#111111",
    accent: "#1a1a1a",
    accentText: "#ffffff",
    danger: "#d93025",
    success: "#188038",
    inputBg: "#f7f7f8",
    inputBorder: "#e3e3e6",
    sidebarBg: "#f7f7f8",
    sidebarBorder: "#e3e3e6",
    pillActive: "#1a1a1a",
    pillActiveText: "#ffffff",
    pillBg: "#ebebed",
    pillText: "#5f5f6b",
    shadow: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#212121",
    bgSub: "#2a2a2a",
    surface: "#303030",
    surfaceHover: "#3a3a3a",
    border: "#3a3a3a",
    borderStrong: "#4e4e4e",
    text: "#ececec",
    textSub: "#9e9e9e",
    textMuted: "#616161",
    userBubble: "#f0f0f0",
    userText: "#111111",
    aiBubble: "#2a2a2a",
    aiBorder: "#3a3a3a",
    aiText: "#ececec",
    accent: "#ececec",
    accentText: "#111111",
    danger: "#f28b82",
    success: "#81c995",
    inputBg: "#2a2a2a",
    inputBorder: "#3a3a3a",
    sidebarBg: "#1a1a1a",
    sidebarBorder: "#2e2e2e",
    pillActive: "#ececec",
    pillActiveText: "#111111",
    pillBg: "#303030",
    pillText: "#9e9e9e",
    shadow: "0 1px 3px rgba(0,0,0,0.3)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.35)",
  },
};

const buildCSS = (t) => `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root {
    min-height: 100%;
    font-family: 'Google Sans Text', 'Google Sans', sans-serif;
    background: ${t.bg};
  }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${t.border}; border-radius: 8px; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .fade-up { animation: fadeUp 0.25s ease forwards; }

  .icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: ${t.textSub};
    cursor: pointer;
    transition: background 0.12s, color 0.12s;
    flex-shrink: 0;
  }

  .icon-btn:hover {
    background: ${t.surface};
    color: ${t.text};
  }

  .field {
    width: 100%;
    background: ${t.inputBg};
    border: 1px solid ${t.inputBorder};
    color: ${t.text};
    border-radius: 14px;
    padding: 15px 16px;
    font-size: 14.5px;
    font-family: 'Google Sans Text', 'Google Sans', sans-serif;
    outline: none;
    transition: border-color 0.12s, background 0.12s;
    min-height: 52px;
  }

  .field:focus {
    border-color: ${t.borderStrong};
  }

  .field::placeholder {
    color: ${t.textMuted};
  }

  .submit-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: none;
    border-radius: 12px;
    background: ${t.accent};
    color: ${t.accentText};
    padding: 13px 18px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: opacity 0.12s, transform 0.12s;
    font-family: 'Google Sans', sans-serif;
    width: 100%;
  }

  .submit-btn:hover:not(:disabled) {
    opacity: .88;
    transform: translateY(-1px);
  }

  .submit-btn:disabled {
    opacity: .55;
    cursor: not-allowed;
  }

  .ghost-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px solid ${t.border};
    border-radius: 12px;
    background: ${t.bg};
    color: ${t.text};
    padding: 13px 18px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    font-family: 'Google Sans', sans-serif;
    transition: background 0.12s, border-color 0.12s;
    text-decoration: none;
  }

  .ghost-btn:hover {
    background: ${t.surface};
    border-color: ${t.borderStrong};
  }

  .material-icons-round {
    font-size: 20px;
    user-select: none;
    line-height: 1;
  }

  .footer {
    margin-top: 24px;
    background: ${t.bgSub};
    border: 1px solid ${t.border};
    border-radius: 18px;
    box-shadow: ${t.shadow};
  }

  .footer-inner {
    padding: 24px;
  }

  .footer-top {
    display: grid;
    grid-template-columns: 1.8fr 1fr 1fr 1fr 1fr;
    gap: 24px;
    margin-bottom: 22px;
  }

  .footer-logo {
    font-size: 22px;
    font-weight: 800;
    color: ${t.text};
    margin-bottom: 10px;
  }

  .footer-logo span {
    color: ${t.textSub};
  }

  .footer-brand p {
    margin: 0;
    font-size: 13px;
    color: ${t.textSub};
    line-height: 1.7;
  }

  .footer-col h4 {
    margin: 0 0 14px;
    font-size: 13px;
    font-weight: 700;
    color: ${t.text};
  }

  .footer-col a {
    display: block;
    margin-bottom: 10px;
    font-size: 13px;
    color: ${t.textSub};
    text-decoration: none;
    transition: color 0.12s ease;
  }

  .footer-col a:hover {
    color: ${t.text};
  }

  .footer-divider {
    height: 1px;
    background: ${t.border};
    margin-bottom: 16px;
  }

  .footer-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .footer-bottom span {
    font-size: 12px;
    color: ${t.textMuted};
  }

  @media (max-width: 768px) {
    .footer {
      border-radius: 16px;
    }

    .footer-top {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 520px) {
    .footer-top {
      grid-template-columns: 1fr;
    }
  }
`;

export default function Signup() {
  const [dark, setDark] = useState(() => localStorage.getItem("darkMode") === "true");
  const [mobile, setMobile] = useState(window.innerWidth <= 768);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    dob: "",
    gender: "",
    country: "",
  });

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const navigate = useNavigate();
  const T = dark ? THEMES.dark : THEMES.light;

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    localStorage.setItem("darkMode", dark);
  }, [dark]);

  useEffect(() => {
    const styleId = "omrali-signup-theme";
    let styleTag = document.getElementById(styleId);

    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    styleTag.innerHTML = buildCSS(T);
  }, [T]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);

    try {
      const q = query(collection(db, "users"), where("email", "==", form.email.trim()));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        setErr("Email already exists.");
        setLoading(false);
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        form.email.trim(),
        form.password
      );

      const uid = userCredential.user.uid;

      await setDoc(doc(db, "users", uid), {
        name: form.name,
        email: form.email.trim(),
        phone: form.phone,
        dob: form.dob,
        gender: form.gender,
        country: form.country,
        createdAt: new Date(),
      });

      localStorage.setItem("email", form.email.trim());
      localStorage.setItem("name", form.name);
      localStorage.setItem("userId", uid);
      localStorage.setItem("phone", form.phone || "");
      localStorage.setItem("dob", form.dob || "");
      localStorage.setItem("gender", form.gender || "");
      localStorage.setItem("country", form.country || "");

      navigate("/");
    } catch (error) {
      console.error("Signup failed:", error);
      setErr(error.message || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const Icon = ({ n, style }) => (
    <span className="material-icons-round" style={style}>
      {n}
    </span>
  );

  const Logo = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 7,
          background: T.accent,
          color: T.accentText,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 14,
          fontFamily: "'Google Sans', sans-serif",
          flexShrink: 0,
        }}
      >
        O
      </div>
      <span
        style={{
          fontWeight: 700,
          fontSize: 15.5,
          color: T.text,
          fontFamily: "'Google Sans', sans-serif",
          letterSpacing: "-0.3px",
        }}
      >
        Omrali <span style={{ fontWeight: 400, color: T.textSub }}>AI</span>
      </span>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text }}>
      {/* Header */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: mobile ? "10px 14px" : "10px 20px",
          borderBottom: `1px solid ${T.border}`,
          background: T.bg,
          minHeight: 56,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="icon-btn" onClick={() => navigate(-1)} title="Back">
            <Icon n="arrow_back" />
          </button>
          <Logo />
        </div>

        <button className="icon-btn" onClick={() => setDark((d) => !d)} title="Toggle theme">
          <Icon n={dark ? "light_mode" : "dark_mode"} />
        </button>
      </header>

      {/* Main */}
      <main
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: mobile ? "18px 14px 28px" : "28px 24px 36px",
        }}
      >
        <div
          className="fade-up"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          {/* Hero */}
          <div
            style={{
              background: T.aiBubble,
              border: `1px solid ${T.aiBorder}`,
              borderRadius: 18,
              padding: mobile ? "18px" : "24px",
              boxShadow: T.shadow,
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: T.textSub,
                marginBottom: 12,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: T.accent,
                  display: "inline-block",
                }}
              />
              Create account
            </div>

            <h1
              style={{
                fontSize: mobile ? 28 : 38,
                lineHeight: 1.1,
                fontWeight: 700,
                letterSpacing: "-0.03em",
                color: T.text,
                marginBottom: 12,
              }}
            >
              Join Omrali AI
            </h1>

            <p
              style={{
                color: T.textSub,
                fontSize: 14.5,
                lineHeight: 1.8,
                maxWidth: 760,
              }}
            >
              Create your account and start using a clean, fast, and modern AI workspace built for conversations that matter.
            </p>
          </div>

          {/* TOP SIGNUP FORM */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <form
              onSubmit={handleSignup}
              style={{
                width: "100%",
                maxWidth: 720,
                background: T.bgSub,
                border: `1px solid ${T.border}`,
                borderRadius: 22,
                padding: mobile ? "18px" : "26px",
                boxShadow: T.shadowMd,
                display: "grid",
                gap: 14,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 2,
                }}
              >
                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 14,
                    background: T.accent,
                    color: T.accentText,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon n="person_add" style={{ fontSize: 22 }} />
                </div>

                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Sign up</div>
                  <div
                    style={{
                      color: T.textSub,
                      fontSize: 13,
                      marginTop: 3,
                    }}
                  >
                    Create your Omrali AI account.
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: mobile ? "1fr" : "1fr 1fr",
                  gap: 14,
                }}
              >
                <div style={{ gridColumn: mobile ? "span 1" : "span 2" }}>
                  <div style={labelStyle(T)}>Full Name</div>
                  <input
                    className="field"
                    type="text"
                    name="name"
                    placeholder="Enter your full name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div style={{ gridColumn: mobile ? "span 1" : "span 2" }}>
                  <div style={labelStyle(T)}>Email Address</div>
                  <input
                    className="field"
                    type="email"
                    name="email"
                    placeholder="Enter your email address"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div style={{ gridColumn: mobile ? "span 1" : "span 2" }}>
                  <div style={labelStyle(T)}>Password</div>
                  <input
                    className="field"
                    type="password"
                    name="password"
                    placeholder="Create a password"
                    value={form.password}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <div style={labelStyle(T)}>Phone Number</div>
                  <input
                    className="field"
                    type="text"
                    name="phone"
                    placeholder="Enter your phone number"
                    value={form.phone}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <div style={labelStyle(T)}>Date of Birth</div>
                  <input
                    className="field"
                    type="date"
                    name="dob"
                    value={form.dob}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <div style={labelStyle(T)}>Gender</div>
                  <select
                    className="field"
                    name="gender"
                    value={form.gender}
                    onChange={handleChange}
                    style={{
                      appearance: "none",
                      WebkitAppearance: "none",
                      MozAppearance: "none",
                    }}
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <div style={labelStyle(T)}>Country</div>
                  <input
                    className="field"
                    type="text"
                    name="country"
                    placeholder="Enter your country"
                    value={form.country}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {err && (
                <div
                  style={{
                    background: `${T.danger}18`,
                    color: T.danger,
                    border: `1px solid ${T.danger}33`,
                    borderRadius: 12,
                    padding: "12px 14px",
                    fontSize: 13.5,
                    lineHeight: 1.5,
                  }}
                >
                  {err}
                </div>
              )}

              <button type="submit" disabled={loading} className="submit-btn">
                <Icon n={loading ? "hourglass_empty" : "person_add"} />
                {loading ? "Creating account..." : "Create account"}
              </button>

              <a href="/" className="ghost-btn" style={{ width: "100%" }}>
                <Icon n="login" />
                Already have an account? Log in
              </a>

              <a href="/about" className="ghost-btn" style={{ width: "100%" }}>
                <Icon n="info" />
                Know about Omrali
              </a>

              <div
                style={{
                  border: `1px solid ${T.border}`,
                  background: T.bg,
                  borderRadius: 14,
                  padding: "12px 14px",
                  color: T.textSub,
                  fontSize: 12.5,
                  lineHeight: 1.6,
                }}
              >
                By signing up, your profile details will be securely stored so you can manage them later from your account.
              </div>
            </form>
          </div>

          {/* INFO SECTION BELOW FORM */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: mobile ? "1fr" : "1.2fr 1fr 1fr",
              gap: 18,
            }}
          >
            <div
              style={{
                background: T.bgSub,
                border: `1px solid ${T.border}`,
                borderRadius: 18,
                padding: mobile ? "16px" : "22px",
                boxShadow: T.shadow,
                display: "grid",
                gap: 14,
              }}
            >
              <div
                style={{
                  borderRadius: 16,
                  padding: mobile ? "18px" : "22px",
                  background: T.bg,
                  border: `1px solid ${T.border}`,
                }}
              >
                <div
                  style={{
                    width: 54,
                    height: 54,
                    borderRadius: 16,
                    background: T.accent,
                    color: T.accentText,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 14,
                  }}
                >
                  <Icon n="auto_awesome" style={{ fontSize: 26 }} />
                </div>

                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: T.text,
                    lineHeight: 1.2,
                    marginBottom: 10,
                  }}
                >
                  Start smarter,
                  <br />
                  from day one.
                </div>

                <p
                  style={{
                    color: T.textSub,
                    fontSize: 14,
                    lineHeight: 1.8,
                  }}
                >
                  Create your account and unlock a smooth Omrali AI experience designed for productivity, clarity, and scale.
                </p>
              </div>
            </div>

            {[
              {
                icon: "shield",
                title: "Secure onboarding",
                desc: "Your account is created using Firebase authentication.",
              },
              {
                icon: "manage_accounts",
                title: "Editable profile",
                desc: "Update your details later from your profile page.",
              },
              {
                icon: "public",
                title: "Global ready",
                desc: "Store location and personal details for a tailored experience.",
              },
            ].map((item) => (
              <div
                key={item.title}
                style={{
                  background: T.bgSub,
                  border: `1px solid ${T.border}`,
                  borderRadius: 18,
                  padding: mobile ? "16px" : "22px",
                  boxShadow: T.shadow,
                }}
              >
                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 14,
                    background: T.aiBubble,
                    border: `1px solid ${T.aiBorder}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 14,
                  }}
                >
                  <Icon n={item.icon} style={{ fontSize: 22, color: T.text }} />
                </div>

                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: T.text,
                    marginBottom: 8,
                  }}
                >
                  {item.title}
                </div>

                <div
                  style={{
                    fontSize: 13.5,
                    color: T.textSub,
                    lineHeight: 1.7,
                  }}
                >
                  {item.desc}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="footer fade-up">
            <div className="footer-inner">
              <div className="footer-top">
                <div className="footer-brand">
                  <div className="footer-logo">
                    Omrali <span>AI</span>
                  </div>
                  <p>
                    Next-generation conversational AI for businesses and developers.
                    Built with care, designed to scale.
                  </p>
                </div>

                <div className="footer-col">
                  <h4>Product</h4>
                  <a href="https://chat.omrali.com">Chat</a>
                </div>

                <div className="footer-col">
                  <h4>Company</h4>
                  <a href="/about">About us</a>
                  <a href="/waitlist">Beta</a>
                  <a href="/contact">Contact</a>
                </div>

                <div className="footer-col">
                  <h4>Legal</h4>
                  <a href="/privacy">Privacy policy</a>
                  <a href="/terms">Terms of service</a>
                </div>

                <div className="footer-col">
                  <h4>Social</h4>
                  <a href="https://www.instagram.com/omrali.ai/">Instagram</a>
                  <a href="https://twitter.com/omrali_ai">Twitter</a>
                </div>
              </div>

              <div className="footer-divider" />

              <div className="footer-bottom">
                <span>© 2026 Omrali AI. All rights reserved.</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const labelStyle = (T) => ({
  fontSize: 12,
  fontWeight: 600,
  color: T.textSub,
  marginBottom: 7,
  paddingLeft: 2,
  textTransform: "uppercase",
  letterSpacing: ".04em",
});